import styled from "styled-components";
import { ArrowTrendingUpIcon } from "@heroicons/react/24/outline";
import { Button } from "@sablier/v2-components/molecules";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { Card as Base } from "~/client/components";
import { Single as Extension } from "~/client/extensions";
import {
  useExtension,
  useFormPrepare,
  useFormSimulation,
  useTimeFlip,
} from "../logic";
import { useInit, useWatcher } from "../system";
import Fields from "./fields";

const WrapperPartial = styled(Base)`
  flex: 1;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: ${(props) => props.theme.sizes.formEdge};
  width: 100%;
  padding-top: calc(${(props) => props.theme.sizes.formEdge} * 1);
  column-gap: ${(props) => props.theme.sizes.edge};

  & > * {
    grid-column: span 2;
    &:nth-child(1),
    &:nth-child(2) {
      grid-column: span 1;
    }
  }
`;

const Extended = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: ${(props) => props.theme.sizes.formEdge};
  width: 100%;
  column-gap: ${(props) => props.theme.sizes.edge};
  & > * {
    grid-column: span 2;
  }

  &:not(:empty) {
    padding-top: calc(${(props) => props.theme.sizes.formEdge} * 1);
    border-top: 2px solid ${(props) => props.theme.colors.border};
  }
`;

const Moment = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: calc(${(props) => props.theme.sizes.edge} * 1);
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxXL} {
    width: 100%;
    ${Grid} {
      grid-template-columns: 1fr;
      row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
      & > * {
        grid-column: span 1;
      }
    }
  }

  ${(props) => props.theme.medias.maxLG} {
    ${Grid} {
      grid-template-columns: 1fr 1fr;
      width: 100%;
    }
  }
  ${(props) => props.theme.medias.maxMD} {
    & > *[data-component="card"] {
      padding-inline: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
      border-radius: 0;
    }
    ${Grid} {
      grid-template-columns: 1fr;
    }
    ${Extended} {
      grid-template-columns: 1fr;
    }
  }
  ${(props) => props.theme.medias.maxSM} {
    ${Moment} {
      grid-template-columns: 1fr ${(props) => props.theme.sizes.inputBox};
      column-gap: 4px;
      & > *:first-child {
        grid-column-start: 1;
        grid-column-end: -1;
      }
    }
  }
`;

const Actions = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    column-gap: 4px;
    *[data-disabled="true"] {
      border: 2px solid ${(props) => props.theme.colors.dark050} !important;
      background-color: ${(props) => props.theme.colors.dark050} !important;
    }
  }
`;

function Extra() {
  const { t } = useT();
  const { isPrepared } = useFormPrepare();
  const { simulate } = useFormSimulation();

  return (
    <Actions>
      <Button
        appearance={"outline"}
        accent={"dark400"}
        isMini
        onClick={simulate}
        isDisabled={!isPrepared}
        left={ArrowTrendingUpIcon}
        title={t("words.simulation")}
      />
    </Actions>
  );
}
function Form() {
  useInit();
  useWatcher();

  const { extension } = useExtension();
  const { isDuration } = useTimeFlip();
  const { t } = useT();

  return (
    <Wrapper title={t("words.details")} extra={<Extra />}>
      <Grid>
        <Fields.Shape />
        <Fields.Cancelability />
        <Fields.Token />
        <Fields.Amount />
        <Fields.Address />
        {isDuration ? (
          <Fields.Duration />
        ) : (
          <Moment>
            <Fields.Start />
            <Fields.End />
            <Fields.Switch />
          </Moment>
        )}
        <Extended>
          <Extension purpose={extension.field.purpose} />
        </Extended>
      </Grid>
    </Wrapper>
  );
}

export default Form;
